/*
 Cookies manager for the client-side and SSR

 Props for the getCookies:
 @key: string;

 Props for the setCookies:
 @key: string;
 @value: string;
 @opts: { expires?: string; path?: string; domain?: string; secure?: string }
*/

import Cookies from 'cookies';
import { IncomingMessage, ServerResponse } from 'http';

export const parseCookieValue = (val?: string) => {
  if (val) {
    try {
      return JSON.parse(val);
    } catch (e) {
      // ignore invalid values
    }
  }
};

const formatCookieValue = (val: Record<string, unknown>) => {
  try {
    return JSON.stringify(val);
  } catch (e) {
    return '{}';
    // ignore invalid values
  }
};

export const getCookie = (key: string) => {
  const cookies = document.cookie.split(/;\s*/);

  for (let i = 0; i < cookies.length; i++) {
    const cookieParts = cookies[i].split('=');
    const cookieKey = decodeURI(cookieParts[0]);
    if (cookieKey === key) return parseCookieValue(decodeURI(cookieParts[1]));
  }

  return undefined;
};

export const getRawCookie = (key: string) => {
  const cookies = document.cookie.split(/;\s*/);

  for (let i = 0; i < cookies.length; i++) {
    const cookieParts = cookies[i].split('=');
    const cookieKey = decodeURI(cookieParts[0]);
    if (cookieKey === key) return decodeURI(cookieParts[1]);
  }

  return undefined;
};

export const clearCookies = (prefix: string, domain?: string, secure?: boolean) => {
  const cookies = document.cookie.split(/;\s*/);

  for (let i = 0; i < cookies.length; i++) {
    const cookieParts = cookies[i].split('=');
    const cookieKey = decodeURI(cookieParts[0]);
    if (cookieKey.startsWith(prefix)) {
      removeCookie(cookieKey, domain, secure);
    }
  }

  return undefined;
};

export const removeCookie = (key: string, domain?: string, secure?: boolean) => {
  setRawCookie(key, '', {
    expires: 'Thu, 01 Jan 1970 00:00:00 UTC',
    path: '/',
    domain: domain,
    secure: secure
  });
  setRawCookie(key, '', {
    expires: 'Thu, 01 Jan 1970 00:00:00 UTC',
    path: '/',
    secure: secure
  });
};

export const setCookie = (
  key: string,
  value: Record<string, unknown>,
  opts?: {
    expires?: string;
    path?: string;
    domain?: string;
    sameSite?: 'strict' | 'lax' | 'none';
    secure?: boolean;
  }
) => {
  return setRawCookie(key, encodeURI(formatCookieValue(value)), opts);
};

export const setRawCookie = (
  key: string,
  value: string,
  opts?: {
    expires?: string;
    path?: string;
    domain?: string;
    sameSite?: 'strict' | 'lax' | 'none';
    secure?: boolean;
  }
) => {
  if (!opts) opts = {};

  let cookie = encodeURI(key) + '=' + value;
  if (opts.expires) cookie += '; expires=' + opts.expires;
  cookie += '; path=' + (opts.path ? encodeURI(opts.path) : '/');
  if (opts.domain) cookie += '; domain=' + encodeURI(opts.domain);
  if (opts.sameSite) cookie += '; sameSite=' + encodeURI(opts.sameSite);
  if (opts.secure) cookie += '; secure';

  document.cookie = cookie;

  return cookie;
};

export const SSRCookies = (req: IncomingMessage, res: ServerResponse, view: string) => {
  const cookies = new Cookies(req, res);
  const cookieValue = cookies.get(view);

  return { props: { [view]: cookieValue || '' } };
};
